import { apiRequest, apiRequestPaged, buildHeaders, buildURL, handleApiError } from './commons/CommonService.Functions';
import { HttpErrorHandler } from '../functions/common';

// models
import { ProviderFileViewModel } from '../models/Entities/AzureFiles/AzureFileUpload';
import {
  DeleteImportedConsumptionsResult,
  GenerateProtocolConsumptionsResult,
  ImportedConsumptionsFilter,
  ImportedConsumptionsPage,
  ImportedProviderConsumption,
  ImportConflictReason,
  ImportItemError,
  ImportPreview,
  ImportPreviewItem,
  ImportProviderFileConsumptionsRequest,
  ImportJobConflictError,
  ImportRequestError,
  ImportStartResponse,
  PreviewItemIds,
  PreviewItemsFilter,
  PreviewItemsPage,
  PreviewItemsQuery,
  ProviderConsumptionAssignmentRequest,
  ProtocolReferenceTemplate,
  ProviderMedicalServiceMapping,
  SaveReferenceTemplateRequest,
  ServiceMappingsPage,
  ServiceMappingsQuery,
  TemplateTestRequest,
  TemplateTestResult,
} from '../models/Entities/ProviderConsumptionImport/ProviderConsumptionImport';
import { normalizeParts, partsForRequest } from '../functions/ProtocolReferenceTemplate.Functions';
import { ConsumptionOriginEnum } from '../models/Enums/ConsumptionOriginEnum';
import { ErrorType } from '../models/HttpError';
import { IPagedResult } from '../models/HttpPagedResult';

/**
 * Importación de consumos de proveedor (spec 005, contrato §5.2). TODAS las llamadas
 * a los endpoints nuevos de MedicalProtocols están acá. El gateway mapea
 * /protocols/{everything} → MedicalProtocols API /{everything}.
 */

const Gateway = process.env.REACT_APP_BILLER_API_GATEWAY;
const ProvidersURL = () => `${Gateway}protocols/providers`;

/** Tamaño de página para leer los archivos del proveedor (la API acepta hasta 500). */
export const PROVIDER_FILES_PAGE_SIZE = 500;

const query = (params: Record<string, string | number | undefined>): string => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== '') search.append(key, String(value));
  });
  const text = search.toString();
  return text ? `?${text}` : '';
};

const json = (method: string, body?: unknown): RequestInit => ({
  method,
  body: body === undefined ? undefined : JSON.stringify(body),
});

/** Envoltura con `succeeded: false` (200 con errores de negocio) → ErrorType con los `errors[]` de la API. */
const unwrap = <T>(result: IPagedResult<T> | undefined, status = 200): IPagedResult<T> | undefined => {
  if (result && result.succeeded === false) {
    const error: ErrorType = {
      ErrorCode: status,
      ErrorMessage: result.message || 'Operation failed',
      Errors: result.errors ?? [],
    };
    throw error;
  }
  return result;
};

const request = async <T>(url: string, token: string, context: string, options: RequestInit = {}): Promise<IPagedResult<T> | undefined> =>
  unwrap(await apiRequestPaged<T>(url, token, context, options));

/**
 * URL de los archivos del proveedor (ProviderId int). Sin `FileType`: desde MP 029 se importa desde archivos de tipo
 * factura (1) o servicios realizados (2); el filtro por tipo y extensión lo hace el front (`isImportableFile`).
 */
export const ProviderFilesURL = (providerId: number): string =>
  buildURL(ProvidersURL(), providerId, 'files') + query({ PageNumber: 1, PageSize: PROVIDER_FILES_PAGE_SIZE });

/** URL de la importación de un archivo (ProviderId int, FileId Guid). */
export const ImportConsumptionsURL = (providerId: number, fileId: string | number): string =>
  buildURL(ProvidersURL(), providerId, 'files', fileId, 'import-consumptions');

/** URL de la vista previa del mapeo (MP 029 §1). */
export const ImportPreviewURL = (providerId: number, fileId: string | number): string =>
  buildURL(ProvidersURL(), providerId, 'files', fileId, 'import-consumptions', 'preview');

/** URL de la eliminación de los consumos importados de un archivo (MP 029 §3). */
export const ImportedConsumptionsOfFileURL = (providerId: number, fileId: string | number): string =>
  buildURL(ProvidersURL(), providerId, 'files', fileId, 'imported-consumptions');

/** URL de los consumos importados del proveedor (ProviderId Guid = person.personIdentifier). */
export const ImportedConsumptionsURL = (providerIdentifier: string, filter: ImportedConsumptionsFilter): string =>
  buildURL(ProvidersURL(), providerIdentifier, 'consumptions') +
  query({
    Origin: ConsumptionOriginEnum.ProviderFileImport,
    ProviderFileId: filter.providerFileId,
    AssignmentStatus: filter.assignmentStatus,
    PageNumber: filter.pageNumber,
    PageSize: filter.pageSize,
  });

export const AssignmentURL = (): string => `${ProvidersURL()}/consumptions/assignment`;
export const GenerateProtocolConsumptionsURL = (): string => `${ProvidersURL()}/consumptions/generate-protocol-consumptions`;

/** GET providers/{ProviderId}/files → archivos del proveedor (todos los tipos). */
export const GetImportableProviderFiles = async (token: string, providerId: number): Promise<ProviderFileViewModel[]> =>
  (await request<ProviderFileViewModel[]>(ProviderFilesURL(providerId), token, 'GetImportableProviderFiles'))?.data ?? [];

/** POST providers/{ProviderId}/files/{FileId}/import-consumptions/preview { valueColumn?, mappingOverride? } (no escribe nada). */
export const PreviewProviderFileImport = async (
  token: string,
  providerId: number,
  fileId: string | number,
  body: ImportProviderFileConsumptionsRequest = {}
): Promise<ImportPreview | undefined> =>
  (await request<ImportPreview>(ImportPreviewURL(providerId, fileId), token, 'PreviewProviderFileImport', json('POST', body)))?.data;

/**
 * POST providers/{ProviderId}/files/{FileId}/import-consumptions.
 * Sin vista previa congelada: `{ valueColumn?, mappingOverride? }` (007/008). Con MP 031: `{ previewId, selectedItemIds,
 * defaultAssignment?, itemAssignments?, defaultMedicalService?, itemMedicalServices?, rememberServiceMappings? }`.
 * MP 030 (spec 008): **202** con `data = job` (la importación corre en segundo plano). Con un backend anterior
 * responde 200 con el resumen; el llamador distingue con `isImportJob`.
 * Errores (`ImportRequestError`): 409 con `data = { reason, jobId? }` → `Reason` (PreviewExpired / ImportInProgress /
 * MappingConflict) y `JobId` (ya hay una importación en curso); 409 sin `jobId` → ErrorType 409 (otro mapeo, spec 007);
 * 400 con `data.itemErrors` → `ItemErrors` (MP 031); el resto, ErrorType con los `errors[]` de la API.
 * No usa `apiRequestPaged` porque su manejo de errores descarta el cuerpo de error, que acá trae `data`.
 */
export const ImportProviderFileConsumptions = async (
  token: string,
  providerId: number,
  fileId: string | number,
  body: ImportProviderFileConsumptionsRequest = {}
): Promise<ImportStartResponse> => {
  const context = 'ImportProviderFileConsumptions';
  let response: Response;
  try {
    response = await fetch(ImportConsumptionsURL(providerId, fileId), { ...json('POST', body), headers: buildHeaders(token) });
  } catch (error: unknown) {
    throw await handleApiError(error, context);
  }
  if (!response.ok) {
    console.error(`Error in ${context}`, response.status);
    const text = await response.text().catch(() => '');
    const failure: ImportRequestError = HttpErrorHandler(text || null, response);
    const extra = importErrorsFromBody(text);
    if (response.status === 409) {
      if (extra.reason) failure.Reason = extra.reason;
      if (extra.jobId) {
        const conflict: ImportJobConflictError = { ...failure, ErrorCode: 409, JobId: extra.jobId };
        throw conflict;
      }
    }
    if (response.status === 400 && extra.itemErrors.length > 0) failure.ItemErrors = extra.itemErrors;
    throw failure;
  }
  const envelope = (await response.json()) as IPagedResult<ImportStartResponse> | undefined;
  return unwrap(envelope, response.status)?.data ?? undefined;
};

const IMPORT_CONFLICT_REASONS: ImportConflictReason[] = ['PreviewExpired', 'ImportInProgress', 'MappingConflict'];

/**
 * Lo que trae `data` en el cuerpo de error de la importación (MP 031): `reason` y `jobId` del 409 e `itemErrors` del 400.
 * Tolera PascalCase y cuerpos que no son JSON (devuelve vacío).
 */
export const importErrorsFromBody = (
  text: string | null | undefined
): { reason?: ImportConflictReason; jobId?: string; itemErrors: ImportItemError[] } => {
  if (!text) return { itemErrors: [] };
  let parsed: any;
  try {
    parsed = JSON.parse(text);
  } catch {
    return { itemErrors: [] };
  }
  const data = parsed?.data ?? parsed?.Data ?? {};
  const rawReason = data?.reason ?? data?.Reason;
  const reason = IMPORT_CONFLICT_REASONS.find((value) => value === rawReason);
  const rawJobId = data?.jobId ?? data?.JobId ?? parsed?.jobId ?? parsed?.JobId;
  const rawItems = data?.itemErrors ?? data?.ItemErrors;
  const itemErrors: ImportItemError[] = Array.isArray(rawItems)
    ? rawItems
        .filter((entry: any) => entry && typeof entry === 'object')
        .map((entry: any) => ({
          itemId: entry.itemId ?? entry.ItemId ?? null,
          rowNumber: entry.rowNumber ?? entry.RowNumber ?? null,
          field: entry.field ?? entry.Field ?? 'selection',
          message: String(entry.message ?? entry.Message ?? ''),
        }))
    : [];
  return { reason, jobId: rawJobId ? String(rawJobId) : undefined, itemErrors };
};

/** `data.jobId` (o `jobId` suelto) del cuerpo de un 409; undefined si no es JSON o no lo trae. */
export const conflictJobIdFromBody = (text: string | null | undefined): string | undefined => importErrorsFromBody(text).jobId;

// ---------------------------------------------------------------------------------------------
// Vista previa congelada (spec 009, MedicalProtocols 031 §1)
// ---------------------------------------------------------------------------------------------

/** Query de `items` / `item-ids`: `duplicateStatus` repetido y los filtros vacíos omitidos. */
const previewItemsQuery = (filter: PreviewItemsFilter, paging?: { pageNumber: number; pageSize: number; withSuggestions?: boolean }): string => {
  const search = new URLSearchParams();
  if (paging) {
    search.append('pageNumber', String(paging.pageNumber));
    search.append('pageSize', String(paging.pageSize));
  }
  (filter.duplicateStatus ?? []).forEach((status) => search.append('duplicateStatus', String(status)));
  const text = filter.search?.trim();
  if (text) search.append('search', text);
  if (filter.suggestedProtocolId !== undefined) search.append('suggestedProtocolId', String(filter.suggestedProtocolId));
  if (filter.suggestedPatientNumber?.trim()) search.append('suggestedPatientNumber', filter.suggestedPatientNumber.trim());
  if (filter.serviceSourceKey?.trim()) search.append('serviceSourceKey', filter.serviceSourceKey.trim());
  if (paging?.withSuggestions === false) search.append('withSuggestions', 'false');
  const value = search.toString();
  return value ? `?${value}` : '';
};

/** URL base de una vista previa congelada (ProviderId int, PreviewId Guid). */
export const ImportPreviewItemsBaseURL = (providerId: number, previewId: string): string =>
  buildURL(ProvidersURL(), providerId, 'consumptions', 'import-previews', previewId);

/** GET providers/{int}/consumptions/import-previews/{previewId}/items?pageNumber=&pageSize=&duplicateStatus=…&search=… */
export const PreviewItemsURL = (providerId: number, previewId: string, query: PreviewItemsQuery): string =>
  `${ImportPreviewItemsBaseURL(providerId, previewId)}/items${previewItemsQuery(query, query)}`;

/** GET providers/{int}/consumptions/import-previews/{previewId}/item-ids?duplicateStatus=…&search=… */
export const PreviewItemIdsURL = (providerId: number, previewId: string, filter: PreviewItemsFilter = {}): string =>
  `${ImportPreviewItemsBaseURL(providerId, previewId)}/item-ids${previewItemsQuery(filter)}`;

/**
 * Página de ítems de la vista previa congelada (PagedResponse). 404 = vista previa vencida, reemplazada o ajena
 * (ErrorType 404: el asistente la regenera); 400 = paginado inválido.
 */
export const GetPreviewItems = async (token: string, providerId: number, previewId: string, query: PreviewItemsQuery): Promise<PreviewItemsPage> => {
  const result = await request<ImportPreviewItem[]>(PreviewItemsURL(providerId, previewId, query), token, 'GetPreviewItems');
  const items = result?.data ?? [];
  return {
    items,
    pageNumber: result?.pageNumber ?? query.pageNumber,
    pageSize: result?.pageSize ?? query.pageSize,
    totalRecords: result?.totalRecords ?? items.length,
  };
};

/** Ids e importes de los ítems seleccionables que cumplen los filtros, sin paginar (404 como `GetPreviewItems`). */
export const GetPreviewItemIds = async (
  token: string,
  providerId: number,
  previewId: string,
  filter: PreviewItemsFilter = {}
): Promise<PreviewItemIds> => {
  const data = (await request<PreviewItemIds>(PreviewItemIdsURL(providerId, previewId, filter), token, 'GetPreviewItemIds'))?.data;
  const items = (data?.items ?? []).filter((item) => !!item?.id).map((item) => ({ ...item, id: String(item.id) }));
  return { items, amount: Number(data?.amount ?? 0) };
};

// ---------------------------------------------------------------------------------------------
// Correspondencias de servicio del proveedor (spec 009, US6, MedicalProtocols 031 §2b)
// ---------------------------------------------------------------------------------------------

/** URL de las correspondencias del proveedor (ProviderId int); con `mappingId`, la de una correspondencia. */
export const ServiceMappingsURL = (providerId: number, mappingId?: string): string =>
  mappingId
    ? buildURL(ProvidersURL(), providerId, 'medical-service-mappings', mappingId)
    : buildURL(ProvidersURL(), providerId, 'medical-service-mappings');

/** GET providers/{int}/medical-service-mappings?search=&pageNumber=&pageSize= (más recientes primero; pageSize 1..200). */
export const GetServiceMappings = async (token: string, providerId: number, filter: ServiceMappingsQuery): Promise<ServiceMappingsPage> => {
  const url =
    ServiceMappingsURL(providerId) + query({ search: filter.search?.trim() || undefined, pageNumber: filter.pageNumber, pageSize: filter.pageSize });
  const result = await request<ProviderMedicalServiceMapping[]>(url, token, 'GetServiceMappings');
  const items = result?.data ?? [];
  return {
    items,
    pageNumber: result?.pageNumber ?? filter.pageNumber,
    pageSize: result?.pageSize ?? filter.pageSize,
    totalRecords: result?.totalRecords ?? items.length,
  };
};

/**
 * PUT providers/{int}/medical-service-mappings/{mappingId} { medicalServiceId } → correspondencia actualizada
 * (400 servicio inexistente o inactivo; 404).
 */
export const UpdateServiceMapping = async (
  token: string,
  providerId: number,
  mappingId: string,
  medicalServiceId: number
): Promise<ProviderMedicalServiceMapping | undefined> =>
  (
    await request<ProviderMedicalServiceMapping>(ServiceMappingsURL(providerId, mappingId), token, 'UpdateServiceMapping', json('PUT', { medicalServiceId }))
  )?.data;

/** DELETE providers/{int}/medical-service-mappings/{mappingId} → 204 (borrado físico); 404 si no existe. */
export const DeleteServiceMapping = async (token: string, providerId: number, mappingId: string): Promise<void> => {
  await apiRequest<void>(ServiceMappingsURL(providerId, mappingId), token, 'DeleteServiceMapping', json('DELETE'));
};

// ---------------------------------------------------------------------------------------------
// Formatos de referencia de protocolo (spec 009, US5, MedicalProtocols 031 §3)
// ---------------------------------------------------------------------------------------------

/** URL de las plantillas del proveedor (ProviderId int); con segmentos, la de una plantilla, `order` o `test`. */
export const ReferenceTemplatesURL = (providerId: number, ...segments: string[]): string =>
  buildURL(ProvidersURL(), providerId, 'protocol-reference-templates', ...segments);

/** Plantilla del servidor con las partes normalizadas y `active` / `order` con valor. */
export const normalizeReferenceTemplate = (raw: ProtocolReferenceTemplate): ProtocolReferenceTemplate => ({
  ...raw,
  id: String(raw.id),
  name: raw.name ?? '',
  order: Number(raw.order ?? 0),
  active: raw.active !== false,
  parts: normalizeParts(raw.parts),
});

const templateBody = (body: SaveReferenceTemplateRequest): SaveReferenceTemplateRequest => {
  const result: SaveReferenceTemplateRequest = {
    name: body.name.trim(),
    parts: partsForRequest(body.parts),
    sampleText: body.sampleText?.trim() || null,
  };
  if (body.active !== undefined) result.active = body.active;
  return result;
};

const templateList = (data: ProtocolReferenceTemplate[] | undefined): ProtocolReferenceTemplate[] =>
  (data ?? []).map(normalizeReferenceTemplate).sort((a, b) => a.order - b.order);

/** GET providers/{int}/protocol-reference-templates → todas (activas e inactivas) en orden de aplicación. */
export const GetReferenceTemplates = async (token: string, providerId: number): Promise<ProtocolReferenceTemplate[]> =>
  templateList((await request<ProtocolReferenceTemplate[]>(ReferenceTemplatesURL(providerId), token, 'GetReferenceTemplates'))?.data);

/** POST providers/{int}/protocol-reference-templates { name, parts, sampleText } → 201 (queda al final); 400; 409 con 20 activas. */
export const CreateReferenceTemplate = async (
  token: string,
  providerId: number,
  body: SaveReferenceTemplateRequest
): Promise<ProtocolReferenceTemplate | undefined> => {
  // El alta no lleva `active` (nace activa).
  const create = templateBody({ ...body, active: undefined });
  const data = (await request<ProtocolReferenceTemplate>(ReferenceTemplatesURL(providerId), token, 'CreateReferenceTemplate', json('POST', create)))
    ?.data;
  return data ? normalizeReferenceTemplate(data) : undefined;
};

/** PUT providers/{int}/protocol-reference-templates/{templateId} { name, parts, sampleText, active? } → 200; 400; 404; 409 al reactivar. */
export const UpdateReferenceTemplate = async (
  token: string,
  providerId: number,
  templateId: string,
  body: SaveReferenceTemplateRequest
): Promise<ProtocolReferenceTemplate | undefined> => {
  const data = (
    await request<ProtocolReferenceTemplate>(
      ReferenceTemplatesURL(providerId, templateId),
      token,
      'UpdateReferenceTemplate',
      json('PUT', templateBody(body))
    )
  )?.data;
  return data ? normalizeReferenceTemplate(data) : undefined;
};

/** DELETE providers/{int}/protocol-reference-templates/{templateId} → 204 (borrado lógico); 404. */
export const DeleteReferenceTemplate = async (token: string, providerId: number, templateId: string): Promise<void> => {
  await apiRequest<void>(ReferenceTemplatesURL(providerId, templateId), token, 'DeleteReferenceTemplate', json('DELETE'));
};

/** PUT providers/{int}/protocol-reference-templates/order { templateIds } (todas) → template[] en el orden nuevo; 400 si falta o sobra alguna. */
export const ReorderReferenceTemplates = async (token: string, providerId: number, templateIds: string[]): Promise<ProtocolReferenceTemplate[]> =>
  templateList(
    (await request<ProtocolReferenceTemplate[]>(ReferenceTemplatesURL(providerId, 'order'), token, 'ReorderReferenceTemplates', json('PUT', { templateIds })))
      ?.data
  );

/** POST providers/{int}/protocol-reference-templates/test { parts, texts } (≤ 500 textos) → un resultado por texto (no guarda). */
export const TestReferenceTemplate = async (token: string, providerId: number, body: TemplateTestRequest): Promise<TemplateTestResult[]> =>
  (
    await request<TemplateTestResult[]>(
      ReferenceTemplatesURL(providerId, 'test'),
      token,
      'TestReferenceTemplate',
      json('POST', { parts: partsForRequest(body.parts), texts: body.texts })
    )
  )?.data ?? [];

/** DELETE providers/{ProviderId}/files/{FileId}/imported-consumptions → { fileId, deleted } (409 todo o nada). */
export const DeleteImportedConsumptionsOfFile = async (
  token: string,
  providerId: number,
  fileId: string | number
): Promise<DeleteImportedConsumptionsResult> => {
  const data = (
    await request<DeleteImportedConsumptionsResult>(ImportedConsumptionsOfFileURL(providerId, fileId), token, 'DeleteImportedConsumptionsOfFile', json('DELETE'))
  )?.data;
  return { fileId: data?.fileId ?? fileId, deleted: data?.deleted ?? 0 };
};

/** GET providers/{ProviderId}/consumptions?Origin=5&ProviderFileId=&AssignmentStatus=&PageNumber=&PageSize= */
export const GetImportedConsumptions = async (
  token: string,
  providerIdentifier: string,
  filter: ImportedConsumptionsFilter
): Promise<ImportedConsumptionsPage> => {
  const result = await request<ImportedProviderConsumption[]>(
    ImportedConsumptionsURL(providerIdentifier, filter),
    token,
    'GetImportedConsumptions'
  );
  const items = result?.data ?? [];
  return {
    items,
    totalRecords: result?.totalRecords ?? items.length,
    pageNumber: result?.pageNumber ?? filter.pageNumber,
    pageSize: result?.pageSize ?? filter.pageSize,
  };
};

/** PUT providers/consumptions/assignment { ids, protocolId, patientNumberInProtocol, patientId? } → consumos actualizados. */
export const AssignProtocolAndPatient = async (
  token: string,
  body: ProviderConsumptionAssignmentRequest
): Promise<ImportedProviderConsumption[]> =>
  (await request<ImportedProviderConsumption[]>(AssignmentURL(), token, 'AssignProtocolAndPatient', json('PUT', body)))?.data ?? [];

/** POST providers/consumptions/generate-protocol-consumptions { ids } → { generated, withWarnings, skipped[] }. */
export const GenerateProtocolConsumptions = async (token: string, ids: string[]): Promise<GenerateProtocolConsumptionsResult> => {
  const data = (
    await request<GenerateProtocolConsumptionsResult>(GenerateProtocolConsumptionsURL(), token, 'GenerateProtocolConsumptions', json('POST', { ids }))
  )?.data;
  return { generated: data?.generated ?? 0, withWarnings: data?.withWarnings ?? 0, skipped: data?.skipped ?? [] };
};

/** URL de un consumo de proveedor (Id Guid). */
export const ProviderConsumptionURL = (id: string): string => buildURL(ProvidersURL(), 'consumptions', id);

/** GET providers/consumptions/{id} → consumo de proveedor (proveedor, archivo y fila de origen). */
export const GetProviderConsumption = async (token: string, id: string): Promise<ImportedProviderConsumption | undefined> =>
  (await request<ImportedProviderConsumption>(ProviderConsumptionURL(id), token, 'GetProviderConsumption'))?.data;
